import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DoneIcon from "@mui/icons-material/Done";
import "./employeeLeaves.css";

const EmployeeLeaves = ({ user, Employee }) => {
  const [counter, setCounter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editAnnual, setEditAnnual] = useState(false);
  const [editPersonal, setEditPersonal] = useState(false);
  const [annualRemaining, setAnnualRemaining] = useState("");
  const [personalRemaining, setPersonalRemaining] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://hr-backend-72v1.onrender.com/leaves?email=${Employee.email}`
      );
      const data = await response.json();
      setCounter(data.leaveSummary);
      setAnnualRemaining(data.leaveSummary.annualLeavesRemaining);
      setPersonalRemaining(data.leaveSummary.personalDaysRemaining);
    } catch (error) {
      console.error("Error fetching leave data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Employee?.email) fetchLeaveData();
  }, [Employee?.email]);

  const handleSaveAnnual = async () => {
    try {
      const response = await fetch(
        "https://hr-backend-72v1.onrender.com/update-leave-counter",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: Employee.email,
            annualLeavesRemaining: Number(annualRemaining),
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setEditAnnual(false);
        setAlertMessage("Annual leaves remaining updated.");
        setShowAlert(true);
        fetchLeaveData();
      }
    } catch (error) {
      console.error("Error updating annual leaves:", error);
    }
  };

  const handleSavePersonal = async () => {
    try {
      const response = await fetch(
        "https://hr-backend-72v1.onrender.com/update-leave-counter",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: Employee.email,
            personalDaysRemaining: Number(personalRemaining),
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setEditPersonal(false);
        setAlertMessage("Personal days remaining updated.");
        setShowAlert(true);
        fetchLeaveData();
      }
    } catch (error) {
      console.error("Error updating personal days:", error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!counter) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <p>No leave data available for this employee.</p>
      </Box>
    );
  }

  return (
    <div className="employee-leaves-container">
      <header className="elHeader">
        <h1 className="elTitle">Leave Balance</h1>
        <p className="elSubtitle">Annual and personal day allowances</p>
      </header>

      <div className="leaves-grid">
        <div className="leave-card">
          <p className="leave-label">Annual Leaves Credited</p>
          <p className="leave-value">{counter.annualLeavesCredited}</p>
        </div>

        <div className="leave-card">
          <p className="leave-label">Annual Leaves Remaining</p>
          <div className="leave-edit-row">
            {!editAnnual ? (
              <p className="leave-value">{counter.annualLeavesRemaining}</p>
            ) : (
              <TextField
                type="number"
                size="small"
                value={annualRemaining}
                onChange={(e) => setAnnualRemaining(e.target.value)}
                sx={{ width: "100px" }}
                inputProps={{ min: 0 }}
              />
            )}
            {!editAnnual ? (
              <Button onClick={() => setEditAnnual(true)}>
                <EditOutlinedIcon />
              </Button>
            ) : (
              <Button onClick={handleSaveAnnual}>
                <DoneIcon />
              </Button>
            )}
          </div>
        </div>

        <div className="leave-card">
          <p className="leave-label">Personal Days Credited</p>
          <p className="leave-value">{counter.personalDaysCredited}</p>
        </div>

        <div className="leave-card">
          <p className="leave-label">Personal Days Remaining</p>
          <div className="leave-edit-row">
            {!editPersonal ? (
              <p className="leave-value">{counter.personalDaysRemaining}</p>
            ) : (
              <TextField
                type="number"
                size="small"
                value={personalRemaining}
                onChange={(e) => setPersonalRemaining(e.target.value)}
                sx={{ width: "100px" }}
                inputProps={{ min: 0 }}
              />
            )}
            {!editPersonal ? (
              <Button onClick={() => setEditPersonal(true)}>
                <EditOutlinedIcon />
              </Button>
            ) : (
              <Button onClick={handleSavePersonal}>
                <DoneIcon />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Snackbar
        open={showAlert}
        autoHideDuration={4000}
        onClose={() => setShowAlert(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setShowAlert(false)} severity="success">
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmployeeLeaves;
